'use client';

import { IconButton } from '@components/button';
import useHeader from '@components/cms-components/header/header.hook';
import TextBlock from '@components/text-block';
import { DoubleArrowDownIcon } from '@radix-ui/react-icons';
import classNames from 'classnames';
import type { Theme } from 'constants/colors';
import type { ReactElement, ReactNode } from 'react';
import React, { useCallback, useMemo, useRef } from 'react';
import type { TextBlockSize } from 'types/components/text-block';
import type { Layout } from 'types/layout';
import { validateString } from 'utils/validate-string';

import styles from './header.module.scss';

interface Props {
  readonly backgroundImage?: boolean | undefined;
  readonly callsToAction?: ReactNode | undefined;
  readonly description?: ReactNode | undefined;
  readonly eyebrow?: ReactNode | undefined;
  readonly fullscreen?: boolean | undefined;
  readonly heading?: ReactNode | undefined;
  readonly layout?: Layout | undefined;
  readonly media?: ReactNode | undefined;
  readonly subHeading?: ReactNode | undefined;
  readonly theme?: Theme | undefined;
  readonly size?: TextBlockSize | undefined;
}

const rootClass = validateString(styles.root);
const scrollAnchorClass = validateString(styles.scrollAnchor);
const contentWrapperClass = validateString(styles.contentWrapper);
const backgroundImageClass = validateString(styles.backgroundImage);

function Header({
  backgroundImage = false,
  description,
  eyebrow,
  fullscreen = false,
  heading,
  layout = 'left',
  subHeading,
  media,
  callsToAction,
  theme,
  size = 'large',
}: Props): ReactElement {
  const { contentClassName, resolvedLayout } = useHeader({
    layout,
    media,
  });

  const scrollAnchorRef = useRef<HTMLDivElement | null>(null);

  const contentItems = useMemo(() => {
    const items: ReactNode[] = [];
    if (typeof media !== 'undefined') {
      items.push(<div key="media">{media}</div>);
    }

    items.push(
      <div
        className={classNames(contentWrapperClass, {
          [backgroundImageClass]: backgroundImage,
        })}
      >
        <TextBlock
          callsToAction={callsToAction}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
          key="text"
          layout={resolvedLayout === 'right' ? 'left' : resolvedLayout}
          size={size}
          subHeading={subHeading}
          theme={theme}
        />
      </div>,
    );

    return layout === 'right' ? items.reverse() : items;
  }, [
    media,
    backgroundImage,
    callsToAction,
    description,
    eyebrow,
    heading,
    resolvedLayout,
    size,
    subHeading,
    theme,
    layout,
  ]);

  const onDownClick = useCallback(() => {
    if (scrollAnchorRef.current !== null) {
      scrollAnchorRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [scrollAnchorRef]);

  return (
    <div className={rootClass}>
      <div className={contentClassName} style={{ position: 'relative' }}>
        {contentItems}
      </div>
      {fullscreen && (
        <>
          <IconButton
            aria-label="Scroll Down"
            circle
            color="primary"
            onClick={onDownClick}
            variant="outline"
          >
            <DoubleArrowDownIcon height={30} width={30} />
          </IconButton>

          <div className={scrollAnchorClass} ref={scrollAnchorRef} />
        </>
      )}
    </div>
  );
}

export default Header;
